import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/services/api-client';
import { postFormText } from '@/shared/api/api-client';

/**
 * POST /api/Master/UploadImage
 *
 * Mirrors Flutter `uploadImage()` in `common_data_source_impl.dart:723`.
 * Takes a base64-encoded image + an area tag and returns the stored URL
 * as a plain string.
 *
 * `area` values are app-wide constants (Flutter `app_constants.dart:716`):
 *   - PATIENT, DOCTOR, STAFF, INSURANCE_CARD, ORGANIZATION, usertemplate, …
 *
 * Multipart variant `/UploadImageFile` exists in Flutter but the
 * base64 JSON variant is the one used by the insurance card uploads and
 * the patient photo flow — keeping a single shape per the simpler path.
 *
 * **Backend response shape (verified 2026-05-20):**
 *   - Happy path: 200 OK + `content-type: application/json` +
 *     JSON-encoded URL string (e.g. `"https://...blob.core.windows.net/..."`).
 *     `apiClient.parseResponse<T>` JSON.parses it back to the unquoted
 *     URL string.
 *   - Dedup / re-upload of identical content: backend returns
 *     200 OK + `content-length: 0` (empty body). `apiClient.parseResponse<T>`
 *     returns `undefined` for empty bodies. Consumers must handle the
 *     `string | undefined` return — see `account-details-page.tsx`
 *     `handleLicensePhoto` for the canonical pattern: optimistically
 *     update form state when a URL came back, otherwise rely on the
 *     downstream query invalidation below to refetch the new URL.
 *
 * **Area-scoped behaviour (settings agent, 2026-05-21 — Task 2):**
 *   - `area: "ORGANIZATION"`: the backend writes the resulting URL to the
 *     `Organization` row's `frontLicenseIdURL` / `backLicenseIdURL` /
 *     `photoUrl` directly (server-side, as a side effect of POST). On
 *     success this hook invalidates the `['settings', 'organization']`
 *     query so the GET refetches and the new URL flows into draft via
 *     `account-details-page.tsx`'s `useEffect(() => setDraft(org), [org])`.
 *     This is what makes the LicensePhotoTile catch up to the new URL
 *     after upload even when the POST returns an empty body.
 *   - `area: "PATIENT"` / `"INSURANCE_CARD"` / others: no auto-invalidation.
 *     The caller takes responsibility for committing the URL to form
 *     state (e.g. `patient-photo-upload.tsx` writes the returned URL
 *     into the patient draft, then a separate PUT persists).
 *
 * **PHI handling (review-hardened on PROD-725 by Samer):**
 *   - Base64 payload is held only in the local `dataUrl` variable
 *     inside the caller's onPick handler — it is not stored in
 *     component state, never written to `localStorage`, and is
 *     dropped when the async call returns. Only the server-issued
 *     URL is persisted to form state.
 *   - No `URL.createObjectURL` blob URLs are created — `<img>`
 *     elements render directly from the server URL once upload
 *     resolves.
 *   - No console / analytics / Sentry log statements anywhere in
 *     the upload path (verified by grep across `src/`).
 *   - Transport: the global `apiClient` injects auth headers and the
 *     request flows over the same HTTPS-only base as every other
 *     `/api/Master/*` call.
 *   - **`gcTime: 0` on the mutation** below — React Query's
 *     MutationCache retains `mutation.variables` (the base64) for
 *     `gcTime` (default 5 min in v5) after settle, surfacing in
 *     Devtools and accessible until eviction. Zeroing the gcTime
 *     evicts the variables immediately on settle, so the base64
 *     blob has no in-memory residency beyond the in-flight request.
 *     Applies to all consumers (patient photo, insurance card, org license).
 */

/**
 * Image-area tag literals — verbatim per Flutter `app_constants.dart:714-719`.
 *
 * The backend reads this string and routes the resulting URL to the right
 * record family. Typing as `string` here would let `"practice"`
 * (lowercase) silently pass type-check and 4xx at the API. Pinning to a
 * union so the compiler catches drift before the request fires
 * (Samer review on PR #346, 2026-05-25).
 *
 * Add a literal here ONLY after confirming the Flutter source ships the
 * exact same value verbatim — magic-strings rule per CLAUDE.md.
 *
 * Consumers should reference `IMAGE_AREAS.STAFF` etc. (below) instead of
 * the raw string — keeps every caller pinned to the single source of
 * truth (Samer review on PR #350, 2026-05-25 Point 2).
 */
export type ImageArea =
  | 'ORGANIZATION'
  | 'PRACTICE'
  | 'PATIENT'
  | 'DOCTOR'
  | 'STAFF'
  | 'INSURANCE_CARD'
  | 'usertemplate';

/**
 * Runtime const for callers — reference `IMAGE_AREAS.STAFF` instead of the
 * literal string `"STAFF"` so the source of truth is centralised. Drift
 * in any single caller would change the value here too. Verbatim per
 * Flutter `app_constants.dart:714-719`.
 */
export const IMAGE_AREAS = {
  ORGANIZATION: 'ORGANIZATION',
  PRACTICE: 'PRACTICE',
  PATIENT: 'PATIENT',
  DOCTOR: 'DOCTOR',
  STAFF: 'STAFF',
  INSURANCE_CARD: 'INSURANCE_CARD',
  USER_TEMPLATE: 'usertemplate',
} as const satisfies Record<string, ImageArea>;

interface UploadImageParams {
  base64String: string;
  area: ImageArea;
}

/** Strip the `data:image/png;base64,` prefix if a DataURL was passed in. */
function stripDataUrlPrefix(s: string): string {
  const i = s.indexOf(',');
  return i >= 0 && s.startsWith('data:') ? s.slice(i + 1) : s;
}

export function useUploadImage() {
  const queryClient = useQueryClient();
  // Return type is `string | undefined` because the backend returns an
  // empty body on dedup / re-upload of identical content. See the file
  // header for the full contract.
  return useMutation<string | undefined, Error, UploadImageParams>({
    // gcTime: 0 evicts mutation.variables (the base64) from the
    // MutationCache as soon as the request settles — PHI safety, see
    // the file header. Don't remove without a corresponding rethink.
    gcTime: 0,
    mutationFn: async ({ base64String, area }) => {
      const body = { base64String: stripDataUrlPrefix(base64String), area };
      return post<string>('/api/Master/UploadImage', body);
    },
    onSuccess: (_url, vars) => {
      // Per the file header §"Area-scoped behaviour": the backend writes
      // ORGANIZATION-area uploads to the org row server-side. Invalidate
      // the org query so the GET refetches and the new URL flows into
      // draft via useEffect on the consumer page. This is what catches
      // the UI up to the new URL when the POST body is empty (which is
      // the common case for dedup re-uploads).
      if (vars.area === 'ORGANIZATION') {
        void queryClient.invalidateQueries({ queryKey: ['settings', 'organization'] });
      }
    },
  });
}

/**
 * Multipart image upload — `POST /api/Master/UploadImageFile`.
 *
 * Mirrors Flutter `uploadImageMultipart` (common_data_source_impl.dart:742):
 * `FormData` with `UploadedImage` (the image file) + `Area`. Returns the
 * stored blob URL as a RAW string (not JSON-quoted, unlike the base64
 * `/UploadImage` variant) — so it goes through `postFormText`, not `post`.
 *
 * Why this exists alongside `useUploadImage`:
 *   On dev the base64 `/api/Master/UploadImage` 200s with an EMPTY body for
 *   every area (see memory `reference_uploadimage_empty_body_dev`), so a
 *   freshly captured image can't be persisted through it. The multipart
 *   endpoint returns the URL and is the path that actually works.
 *
 * **Area is lowercased before sending.** The Master service uses the `Area`
 * value VERBATIM as the Azure blob container name, and Azure requires
 * lowercase container names — an uppercase area (`"PRACTICE"`) makes
 * `BlobContainerClient.CreateIfNotExistsAsync` throw `InvalidResourceName`
 * (HTTP 500), confirmed in the master pod logs 2026-06-18. The backend
 * should lowercase it too (filed); until then this keeps the FE working.
 */
export function useUploadImageFile() {
  return useMutation<string, Error, { blob: Blob; area: ImageArea; filename?: string }>({
    // gcTime: 0 — same PHI rationale as useUploadImage (don't retain the
    // image bytes in the MutationCache after the request settles).
    gcTime: 0,
    mutationFn: async ({ blob, area, filename = 'signature.png' }) => {
      const form = new FormData();
      form.append('UploadedImage', blob, filename);
      form.append('Area', area.toLowerCase());
      return postFormText('/api/Master/UploadImageFile', form);
    },
  });
}

/**
 * Convert a base64 data URL (`data:image/jpeg;base64,XXXX`) to a Blob.
 *
 * The EID desk-scanner hands us the photo as a base64 data URL. To persist it
 * we must go through the MULTIPART `/UploadImageFile` endpoint (which returns
 * the hosted URL), NOT the base64 `/UploadImage` endpoint — the latter 200s
 * with an empty body for `area: "PATIENT"`, so the scanned photo is silently
 * lost (Dr. Johnny's clinic, Noah Nazha, 2026-07-27). This adapts the data URL
 * into the `Blob` that `useUploadImageFile` expects.
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(',');
  const header = comma >= 0 ? dataUrl.slice(0, comma) : '';
  const data = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  // Throw on empty/malformed input rather than silently returning a 0-byte
  // Blob — an empty blob would POST to /UploadImageFile and 400, and the caller
  // must surface that (photo silently missing is the exact bug we're fixing).
  // `atob` below already throws on invalid base64.
  if (!data) throw new Error('dataUrlToBlob: no image data in the provided data URL');
  const mime = header.match(/^data:([^;]+)/i)?.[1] ?? 'image/jpeg';
  // A data URL with no `,` (bare base64) or an explicit `;base64` is base64;
  // anything else is percent-encoded text.
  const isBase64 = comma < 0 || /;base64/i.test(header);
  const binary = isBase64 ? atob(data) : decodeURIComponent(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/** Read a File as a base64 DataURL (browser FileReader). */
// Graduated into Signal DS 1.45.0 — the package implementation is line-for-line the same
// FileReader wrapper that was here.
export { readFileAsBase64 } from '@scripsteam/scrips-signal-ds';
